import React, { useEffect, useState } from "react";
import { FileText, X, RotateCcw, AlertCircle, CheckCircle2, Loader2, Minimize2 } from "lucide-react";
import { fmtSize, DOC_ACCEPT, DOC_MAX_BYTES } from "@shared/consultation/ConsultationDocuments";
import { compressImage, shouldCompress } from "@shared/consultation/imageCompression";

export const MAX_FILES_PER_CONSULTATION = 20;
const EXT_OK = DOC_ACCEPT.split(",").filter((x) => x.startsWith("."));

let seq = 0;
// Build a composer entry for a picked file; oversize / wrong-type files are kept as errors so the user can remove them.
// Large photos start as "preparing" — they are shrunk client-side (see imageCompression.js) before they count against 10 MB.
export function toPending(file) {
  const ext = `.${(file.name.split(".").pop() || "").toLowerCase()}`;
  let error = null;
  if (!EXT_OK.includes(ext)) error = "Unsupported type (PDF, JPG, PNG, WEBP, DOC, DOCX)";
  else if (file.size === 0) error = "Empty file";
  else if (shouldCompress(file)) return { key: `pf${++seq}`, file, status: "preparing", error: null, progress: 0, fatal: false };
  else if (file.size > DOC_MAX_BYTES) error = "File exceeds 10 MB";
  return { key: `pf${++seq}`, file, status: error ? "error" : "queued", error, progress: 0, fatal: !!error };
}

// Resolve a "preparing" entry: compressed file (or original) → queued, or a fatal error if still too big.
export async function prepareImage(it) {
  const file = await compressImage(it.file);
  if (file.size > DOC_MAX_BYTES) return { file, status: "error", error: "File exceeds 10 MB even after compression", fatal: true };
  return { file, status: "queued", originalSize: file.originalSize || null };
}

function Thumb({ file }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    if (!file.type.startsWith("image/")) return undefined;
    const u = URL.createObjectURL(file);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [file]);
  if (url) return <img src={url} alt="" className="h-10 w-10 rounded-lg object-cover shrink-0" />;
  return <div className="h-10 w-10 rounded-lg bg-slate-100 grid place-items-center shrink-0"><FileText size={16} className="text-[#0A2540]" /></div>;
}

// Removable previews of files selected but not yet sent. Uploads start only on Send.
export function ComposerAttachments({ items, onRemove, onRetry, sending }) {
  if (!items.length) return null;
  return (
    <div className="max-h-40 overflow-y-auto flex flex-wrap gap-2 pb-3" data-testid="composer-attachments">
      {items.map((it) => (
        <div key={it.key} className={`relative flex items-center gap-2 rounded-xl border p-2 w-full sm:w-[calc(50%-4px)] ${it.status === "error" ? "border-rose-200 bg-rose-50/60" : "border-slate-200 bg-slate-50"}`}
          data-testid={`composer-attachment-${it.key}`} data-status={it.status}>
          <Thumb file={it.file} />
          <div className="min-w-0 flex-1">
            <p className="text-xs font-bold text-[#0A2540] truncate" title={it.file.name}>{it.file.name}</p>
            {it.status === "error" ? (
              <p className="text-[10px] text-rose-700 inline-flex items-center gap-1" data-testid={`composer-attachment-error-${it.key}`}><AlertCircle size={10} /> {it.error}</p>
            ) : it.status === "uploading" ? (
              <div className="mt-1 flex items-center gap-2">
                <div className="flex-1 h-1.5 rounded-full bg-slate-200 overflow-hidden" aria-label="Upload progress">
                  <div className="h-full bg-[#D4AF37] transition-[width] duration-150" style={{ width: `${it.progress}%` }} data-testid={`composer-attachment-progress-${it.key}`} />
                </div>
                <span className="text-[10px] text-slate-500 shrink-0">{it.progress < 100 ? `${it.progress}%` : "Saving…"}</span>
              </div>
            ) : it.status === "done" ? (
              <p className="text-[10px] text-emerald-700 inline-flex items-center gap-1"><CheckCircle2 size={10} /> Sent</p>
            ) : it.status === "preparing" ? (
              <p className="text-[10px] text-slate-500 inline-flex items-center gap-1" data-testid={`composer-attachment-preparing-${it.key}`}><Loader2 size={10} className="animate-spin" /> Optimising photo…</p>
            ) : it.originalSize ? (
              <p className="text-[10px] text-emerald-700 inline-flex items-center gap-1" data-testid={`composer-attachment-compressed-${it.key}`}><Minimize2 size={10} /> {fmtSize(it.originalSize)} → {fmtSize(it.file.size)}</p>
            ) : (
              <p className="text-[10px] text-slate-500">{fmtSize(it.file.size)}</p>
            )}
          </div>
          {it.status === "error" && !it.fatal && (
            <button type="button" onClick={() => onRetry(it.key)} disabled={sending} aria-label={`Retry ${it.file.name}`} data-testid={`composer-attachment-retry-${it.key}`}
              className="p-1.5 rounded-lg text-[#0A2540] hover:bg-white disabled:opacity-40"><RotateCcw size={13} /></button>
          )}
          {it.status !== "uploading" && (
            <button type="button" onClick={() => onRemove(it.key)} disabled={sending} aria-label={`Remove ${it.file.name}`} data-testid={`composer-attachment-remove-${it.key}`}
              className="p-1.5 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-white disabled:opacity-40"><X size={13} /></button>
          )}
        </div>
      ))}
    </div>
  );
}
